import { useTranslation } from "react-i18next";
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { useParams } from "react-router-dom";
import { getCarWashInfo } from "../services/carWash.service";
import { ICarWashObject } from "../types/carWash";

interface CarWashContextType {
  carWash: ICarWashObject | null;
  loading: boolean;
  error: string | null;
  setError: (error: string | null) => void;
  refetch: () => Promise<void>;
}

const CarWashContext = createContext<CarWashContextType | undefined>(undefined);

export const useCarWash = () => {
  const context = useContext(CarWashContext);
  if (context === undefined) {
    throw new Error("useCarWash must be used within a CarWashProvider");
  }
  return context;
};

interface CarWashProviderProps {
  children: ReactNode;
}

export const CarWashProvider: React.FC<CarWashProviderProps> = ({
  children,
}) => {
  const { t } = useTranslation();
  const { carWashID } = useParams<{ carWashID: string }>();
  const [carWash, setCarWash] = useState<ICarWashObject | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCarWashData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (!carWashID) {
        throw new Error(t("carWashes.missingId"));
      }
      const response = await getCarWashInfo(carWashID);
      setCarWash(response);
    } catch (err: any) {
      setError(err.message || t("carWashes.fetchDataFailed"));
      setCarWash(null);
    } finally {
      setLoading(false);
    }
  }, [carWashID, t]);

  useEffect(() => {
    fetchCarWashData();
  }, [fetchCarWashData]);

  const value: CarWashContextType = {
    carWash,
    loading,
    error,
    setError,
    refetch: fetchCarWashData,
  };

  return (
    <CarWashContext.Provider value={value}>{children}</CarWashContext.Provider>
  );
};
